import React, {useEffect, useMemo, useState} from 'react';
import {Route} from 'react-router-dom';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {
  apiGetAllFdCustomers,
  apiGetAllFdCrops,
  apiCreateFdCustomerSupplier,
  apiDeleteCustomerSupplier,
} from 'constants/Api';
import {ISupplierUserProfile, IGenericType} from 'constants/interfaces';
import {
  FdCustomerSupplierForm,
  Submission,
  OTHER_CUSTOMER_ID,
  LinkInput,
} from 'components/applications/FdCustomerSupplierForm';

type Props = {
  user: ISupplierUserProfile;
  applicationId: number;
  initialLinks: any[];
};

const AddOrUpdateFdCustomers: React.FC<Props> = ({
  applicationId,
  initialLinks,
}) => {
  /* dropdown data */
  const [customers, setCustomers] = useState<{id: number; name: string}[]>([]);
  const [crops, setCrops] = useState<{id: number; name: string}[]>([]);

  /* ui state */
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* ① fetch dropdown lists once */
  useEffect(() => {
    Promise.all([apiGetAllFdCustomers(), apiGetAllFdCrops()]).then(
      ([cust = [], cr = []]) => {
        setCustomers(
          cust.map((c: IGenericType) => ({
            id: Number(c.id),
            name: c.name ?? '',
          })),
        );
        setCrops(
          cr.map((c: IGenericType) => ({
            id: Number(c.id),
            name: c.name ?? '',
          })),
        );
      },
    );
  }, []);

  /* ② map of existing pair-keys → DB row id */
  const existingPairId = useMemo(() => {
    const m = new Map<string, number>();
    initialLinks.forEach((row: any) =>
      m.set(`${row.customer.id}-${row.crop.id}`, Number(row.id)),
    );
    return m;
  }, [initialLinks]);

  const currentKeys = useMemo(
    () => new Set(existingPairId.keys()),
    [existingPairId],
  );

  /* ③ dedupe so each customer appears once in the UI */
  const defaultLinks: LinkInput[] = useMemo(() => {
    const map = new Map<number, {customerId: number; cropIds: number[]}>();
    initialLinks.forEach((row: any) => {
      const custId = Number(row.customer.id);
      if (!map.has(custId)) map.set(custId, {customerId: custId, cropIds: []});
      map.get(custId)!.cropIds.push(Number(row.crop.id));
    });
    const links: LinkInput[] = Array.from(map.values());

    // Load "Other" entries from localStorage
    try {
      const storedOtherEntries = localStorage.getItem(
        `fd-customer-other-${applicationId}`,
      );
      if (storedOtherEntries) {
        const otherEntries: LinkInput[] = JSON.parse(storedOtherEntries);
        links.push(...otherEntries);
      }
    } catch (err) {
      console.error('Error loading Other entries from localStorage', err);
    }

    return links;
  }, [initialLinks, applicationId]);

  /* helper */
  const close = () => reduxToggleSidePanel(false, null);

  /* ④ submit */
  const submit = async (data: Submission) => {
    setLoading(true);

    const desiredKeys = new Set<string>();
    // Skip "Other" entries (they don't have cropIds to create database entries)
    data.links.forEach((l) => {
      if (
        l.customerId !== OTHER_CUSTOMER_ID &&
        l.cropIds &&
        l.cropIds.length > 0
      ) {
        l.cropIds.forEach((cid) => desiredKeys.add(`${l.customerId}-${cid}`));
      }
    });

    /* ---- creates ---- */
    const createOps: Promise<any>[] = [];
    desiredKeys.forEach((k) => {
      if (!currentKeys.has(k)) {
        const [customerId, cropId] = k.split('-').map(Number);
        createOps.push(
          apiCreateFdCustomerSupplier({
            applicationId: Number(applicationId),
            customerId: Number(customerId),
            cropId: Number(cropId),
          }),
        );
      }
    });

    /* ---- deletes ---- */
    const deleteOps: Promise<any>[] = [];
    currentKeys.forEach((k) => {
      if (!desiredKeys.has(k)) {
        const [customerId, cropId] = k.split('-').map(Number);
        deleteOps.push(
          apiDeleteCustomerSupplier({
            applicationId: Number(applicationId),
            customerId: Number(customerId),
            cropId: Number(cropId),
          }),
        );
      }
    });

    await Promise.allSettled([...createOps, ...deleteOps]);

    // Save "Other" entries to localStorage
    const otherEntries = data.links.filter(
      (l) => l.customerId === OTHER_CUSTOMER_ID && l.otherText,
    );
    try {
      if (otherEntries.length > 0) {
        localStorage.setItem(
          `fd-customer-other-${applicationId}`,
          JSON.stringify(otherEntries),
        );
      } else {
        // Remove if no "Other" entries
        localStorage.removeItem(`fd-customer-other-${applicationId}`);
      }
    } catch (err) {
      console.error('Error saving Other entries to localStorage', err);
    }

    setSuccess(true);
    reduxSetSidePanelEditState('saved');
    setLoading(false);
  };

  /* success splash */
  if (success) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">Primary customers updated</div>
            </div>
            <div className="btn-container">
              <button
                className="primary-btn"
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      />
    );
  }

  /* render form */
  return (
    <FdCustomerSupplierForm
      key={`${initialLinks.length}-${defaultLinks.length}`}
      customers={customers}
      crops={crops}
      defaultLinks={defaultLinks}
      isLoading={loading}
      submitButtonTitle="Save"
      onSubmit={submit}
      onCancel={close}
    />
  );
};

export default AddOrUpdateFdCustomers;
